// TypePopperWide.tsx

import * as React from 'react'
import Popper, { PopperProps } from '@mui/material/Popper'

type WidthStrategy = 'match-anchor' | 'fixed' | 'auto'

export type WidePopperProps = Omit<PopperProps, 'strategy'> & {
  widthStrategy?: WidthStrategy
  fixedWidth?: number | string
  maxWidth?: number | string
  zIndex?: number
  offset?: [number, number]
  strategy?: 'absolute' | 'fixed'
}

const TypePopperWide = React.forwardRef<HTMLDivElement, WidePopperProps>(function TypePopperWide(
  {
    widthStrategy = 'match-anchor',
    fixedWidth,
    maxWidth = '90vw',
    zIndex = 2700,
    offset = [0, 6],

    // ✅ keep it anchored by default
    placement = 'bottom-start',
    style,
    modifiers,
    // ✅ for dropdowns inside scrolling dialog content, absolute is usually more “attached”
    // (if you truly need fixed, you still can—but this will feel better in most dialogs)
    strategy = 'absolute',

    ...rest
  },
  ref
) {
  const sameWidth = React.useMemo(
    () => ({
      name: 'sameWidth',
      enabled: widthStrategy === 'match-anchor',
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }: any) => {
        state.styles.popper.width = `${state.rects.reference.width}px`
      },
      effect: ({ state }: any) => {
        if (widthStrategy !== 'match-anchor') return
        const refEl = state.elements.reference as HTMLElement
        if (!refEl) return
        const updateWidth = () => {
          ;(state.elements.popper as HTMLElement).style.width = `${refEl.getBoundingClientRect().width}px`
        }
        const ro = new ResizeObserver(updateWidth)
        ro.observe(refEl)
        updateWidth()
        return () => ro.disconnect()
      },
    }),
    [widthStrategy]
  )

  const trackAnchorLayout = React.useMemo(
    () => ({
      name: 'trackAnchorLayout',
      enabled: true,
      phase: 'write',
      fn: () => {},
      effect: ({ state, instance }: any) => {
        const refEl = state.elements.reference as HTMLElement
        if (!refEl) return
        const ro1 = new ResizeObserver(() => instance.update())
        ro1.observe(refEl)
        const gridItem = refEl.closest('.MuiGrid-item') as HTMLElement | null
        const ro2 = gridItem ? new ResizeObserver(() => instance.update()) : null
        if (gridItem && ro2) ro2.observe(gridItem)
        return () => {
          ro1.disconnect()
          ro2?.disconnect()
        }
      },
    }),
    []
  )

  const computedStyle: React.CSSProperties = {
    ...style,
    maxWidth,
    zIndex,
    transition: 'width 200ms ease',
  }

  const computedModifiers = [
    { name: 'offset', options: { offset } },

    // ✅ keep it inside the scroll/dialog clipping region
    {
      name: 'preventOverflow',
      options: {
        boundary: 'clippingParents',
        altBoundary: true,
        padding: 8,
        tether: true,
        altAxis: true,
      },
    },

    // ✅ this is what makes it open above when near bottom
    {
      name: 'flip',
      enabled: true,
      options: {
        padding: 8,
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
      },
    },

    ...(widthStrategy === 'match-anchor' ? [sameWidth] : []),
    trackAnchorLayout,
    ...(modifiers || []),
  ]

  return (
    <Popper
      {...rest}
      ref={ref}
      placement={placement}
      style={computedStyle}
      modifiers={computedModifiers as any}
      {...({ strategy } as any)}
    />
  )
})

export default TypePopperWide
